import "normalize.css";
import "@/styles/main.css";
import "@/styles/addressPicker.css";
import "@/styles/keenSlider.css";

import { datadogRum } from "@datadog/browser-rum";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { FlagProvider } from "@unleash/nextjs/client";
import { getCookie, setCookie } from "cookies-next";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import {
  appWithTranslation,
  i18n,
  SSRConfig,
  useTranslation,
} from "next-i18next";
import { useEffect, useState } from "react";
import { setLocale } from "yup";

import { Analytics, GoogleTagManagerScript } from "@/components/Analytics";
import { Cart } from "@/components/Cart";
import { Dialog } from "@/components/elements/Dialog";
import { Notification } from "@/components/elements/Notification";
import { LOCALE_COOKIE_NAME } from "@/constants";
import { CartProvider } from "@/hooks/useCart";
import { DialogProvider } from "@/hooks/useDialog";
import { GlobalStateProvider } from "@/hooks/useGlobalState";
import { NotificationProvider } from "@/hooks/useNotification";
import { RemoveScrollBarProvider } from "@/hooks/useRemoveScrollBar";
import { isValidLocale } from "@/utils";

import nextI18NextConfig from "../../next-i18next.config.js";

function MyApp({
  Component,
  pageProps,
}: AppProps<SSRConfig & { dehydratedState: unknown }>) {
  const { t } = useTranslation();
  const { locale, replace, asPath } = useRouter();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  setLocale({
    mixed: {
      default: t("error_generic_input"),
      required: t("error_required"),
    },
  });

  // On mount, if locale cookie isn't set correctly, update it
  useEffect(() => {
    const localeCookie = String(getCookie(LOCALE_COOKIE_NAME));
    if (!isValidLocale(localeCookie)) setCookie(LOCALE_COOKIE_NAME, locale);
  }, [locale]);

  useEffect(() => {
    i18n.on("languageChanged", () =>
      setTimeout(
        () =>
          replace({ pathname: asPath }, undefined, {
            scroll: false,
          }),
        1000
      )
    );
  }, []);

  if (process.env.NODE_ENV != "development") {
    datadogRum.init({
      applicationId: "435758f9-09de-4f2b-81e8-80c3e500145f",
      clientToken: "pubd1f7066a5a69b897a0a239c386f9db0b",
      site: "datadoghq.eu",
      service: "pafyll-frontend",
      env: process.env.VERCEL_ENV === "preview" ? "staging" : "production",
      version: "1.27.0",
      trackUserInteractions: true, // heatmaps enabling(beta)
      trackResources: true, // Enables collection of resource loaded events (CSS, JS, img, json,..)
      trackLongTasks: true, // Enables collection of long task events, task that blocks the main thread for more than 50ms

      allowedTracingUrls: [
        "https://beta.pafyll.com",
        /https:\/\/.*\.pafyll\.com/,
        (url) => url.startsWith("https://beta.pafyll.com"),
      ], // connect tracing with RUM(Type: array) a list of request URLs used to inject tracing headers
      traceSampleRate: 100,
      telemetrySampleRate: 100, // Telemetry data (such as errors and debug logs)

      sessionSampleRate: 100, // collect all sessions if 100
      sessionReplaySampleRate: 100, // if not included, the default is 100
      defaultPrivacyLevel: "mask-user-input", // mask user's input for session replays (mask, allow)
    });
    datadogRum.startSessionReplayRecording();
  }

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width, viewport-fit=cover"
        />
      </Head>
      <GoogleTagManagerScript />
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps?.dehydratedState}>
          <FlagProvider>
            <NotificationProvider>
              <GlobalStateProvider>
                <RemoveScrollBarProvider>
                  <DialogProvider>
                    <CartProvider>
                      <Analytics>
                        <Component {...pageProps} />
                        <Cart />
                        <Notification />
                        <Dialog />
                      </Analytics>
                    </CartProvider>
                  </DialogProvider>
                </RemoveScrollBarProvider>
              </GlobalStateProvider>
            </NotificationProvider>
          </FlagProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </Hydrate>
      </QueryClientProvider>
    </>
  );
}

export default appWithTranslation<
  AppProps<SSRConfig & { dehydratedState: unknown }>
>(MyApp, nextI18NextConfig);
